import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../../lib/api.js';

import { streamChat, type ChatRequest } from './aiClient.js';
import type {
  ChatMessage,
  ContentBlock,
  Conversation,
  ProviderInfo,
  StreamEvent,
} from './types.js';

interface UseAiChatOpts {
  /** Optional starting conversation ID — when set, history is loaded on mount. */
  conversationId?: string | null;
  /** Forwarded as page-context to the orchestrator on every send. */
  pageContext?: ChatRequest['pageContext'];
}

interface UseAiChatReturn {
  messages: ChatMessage[];
  conversationId: string | null;
  isStreaming: boolean;
  error: string | null;
  providers: ProviderInfo[] | null;
  send(text: string, opts?: { provider?: string; model?: string }): Promise<void>;
  cancel(): void;
  reset(): void;
  loadConversation(id: string): Promise<void>;
}

/**
 * Stateful hook for a single AI chat session.
 *
 * - `messages` mutates as deltas arrive so the UI streams character-by-character.
 * - `cancel()` aborts the fetch and freezes the partial assistant message.
 * - `reset()` clears state (useful when the user starts a new chat).
 */
export function useAiChat(opts: UseAiChatOpts = {}): UseAiChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(opts.conversationId ?? null);
  const [isStreaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProviderInfo[] | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let mounted = true;
    void api()
      .get<{ items: ProviderInfo[] }>('/ai/providers')
      .then((r) => { if (mounted) setProviders(r.data.items); })
      .catch(() => { if (mounted) setProviders([]); });
    return () => { mounted = false; };
  }, []);

  const loadConversation = useCallback(async (id: string): Promise<void> => {
    setError(null);
    try {
      const r = await api().get<{
        conversation: Conversation;
        messages: Array<{ id: string; role: ChatMessage['role']; content: ContentBlock[]; createdAt: string }>;
      }>(`/ai/conversations/${id}`);
      setConversationId(r.data.conversation.id);
      setMessages(
        r.data.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.createdAt,
        })),
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  // Initial load if the caller passed an existing conversation ID.
  useEffect(() => {
    if (opts.conversationId) void loadConversation(opts.conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useCallback<UseAiChatReturn['send']>(async (text, opts2 = {}) => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;

    setError(null);
    setStreaming(true);

    // Optimistically append the user message.
    const userMsg: ChatMessage = {
      id: `tmp-user-${Date.now()}`,
      role: 'user',
      content: [{ type: 'text', text: trimmed }],
    };
    // Placeholder assistant message that will be mutated as deltas arrive.
    const assistantId = `tmp-asst-${Date.now()}`;
    const assistantMsg: ChatMessage = { id: assistantId, role: 'assistant', content: [] };

    setMessages((m) => [...m, userMsg, assistantMsg]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const body: ChatRequest = {
      message: trimmed,
      ...(conversationId ? { conversationId } : {}),
      ...(opts2.provider ? { provider: opts2.provider } : {}),
      ...(opts2.model ? { model: opts2.model } : {}),
      ...(opts.pageContext ? { pageContext: opts.pageContext } : {}),
    };

    let textBuf = '';
    const toolUses: Array<{ id: string; name: string; input: Record<string, unknown> }> = [];
    const toolResults = new Map<string, { result: unknown; isError?: boolean; durationMs?: number }>();

    const updateAssistant = (): void => {
      const blocks: ContentBlock[] = [];
      if (textBuf) blocks.push({ type: 'text', text: textBuf });
      for (const tu of toolUses) blocks.push({ type: 'tool_use', id: tu.id, name: tu.name, input: tu.input });
      setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, content: blocks } : msg)));
    };

    try {
      for await (const evt of streamChat(body, ctrl.signal) as AsyncGenerator<StreamEvent>) {
        if (evt.type === 'conversation') {
          setConversationId(evt.conversationId);
        } else if (evt.type === 'text_delta') {
          textBuf += evt.delta;
          updateAssistant();
        } else if (evt.type === 'tool_use') {
          toolUses.push({ id: evt.id, name: evt.name, input: evt.input });
          updateAssistant();
        } else if (evt.type === 'tool_result') {
          toolResults.set(evt.toolUseId, { result: evt.result, ...(evt.isError ? { isError: true } : {}), ...(evt.durationMs !== undefined ? { durationMs: evt.durationMs } : {}) });
          // Append a synthetic tool-result message so the UI shows the call's output.
          setMessages((m) => {
            const existing = m.find((msg) =>
              msg.role === 'tool' &&
              msg.content.some((c) => c.type === 'tool_result' && c.toolUseId === evt.toolUseId),
            );
            if (existing) return m;
            const block: ContentBlock = {
              type: 'tool_result',
              toolUseId: evt.toolUseId,
              content: JSON.stringify(evt.result),
              ...(evt.isError ? { isError: true } : {}),
            };
            const synthetic: ChatMessage = {
              id: `tmp-tool-${evt.toolUseId}`,
              role: 'tool',
              content: [block],
            };
            const out = [...m];
            const asstIdx = out.findIndex((msg) => msg.id === assistantId);
            if (asstIdx === -1) return [...m, synthetic];
            // Insert tool result right before the assistant message so its
            // bubble stays at the bottom while results render above.
            out.splice(asstIdx, 0, synthetic);
            return out;
          });
        } else if (evt.type === 'tool_denied') {
          setError(`Tool ${evt.toolName} denied: ${evt.reason}`);
        } else if (evt.type === 'message_complete') {
          // tokens recorded on the assistant message
          setMessages((m) =>
            m.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    ...(evt.tokensIn !== undefined ? { tokensIn: evt.tokensIn } : {}),
                    ...(evt.tokensOut !== undefined ? { tokensOut: evt.tokensOut } : {}),
                  }
                : msg,
            ),
          );
        } else if (evt.type === 'done') {
          // stream end
        } else if (evt.type === 'error') {
          setError(evt.message);
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setError((e as Error).message);
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [conversationId, isStreaming, opts.pageContext]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
  }, []);

  const reset = useCallback(() => {
    cancel();
    setMessages([]);
    setConversationId(null);
    setError(null);
  }, [cancel]);

  return useMemo(
    () => ({ messages, conversationId, isStreaming, error, providers, send, cancel, reset, loadConversation }),
    [messages, conversationId, isStreaming, error, providers, send, cancel, reset, loadConversation],
  );
}
